/**
 * ThemeContext — gerencia tema claro/escuro/sistema.
 *
 * - "system" (default): segue prefers-color-scheme do OS
 * - "light": força tema claro
 * - "dark":  força tema escuro
 *
 * Persistido em localStorage. Aplica/remove class "dark" no <html>.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

export type ThemeMode = "system" | "light" | "dark";

interface ThemeContextValue {
  mode: ThemeMode;
  /** Resolved (system → light|dark conforme prefers-color-scheme). */
  resolved: "light" | "dark";
  setMode: (m: ThemeMode) => void;
  /** Atalho — alterna entre light/dark (ignora system). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "envpulse:theme";

function getStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function getSystemPreference(): "light" | "dark" {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [systemPref, setSystemPref] = useState<"light" | "dark">(getSystemPreference);

  // Detecta mudança na preferência do sistema (usuário troca dark/light no OS)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemPref(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const resolved = mode === "system" ? systemPref : mode;

  // useLayoutEffect (não useEffect): aplica a class "dark" no <html> ANTES
  // do browser fazer o primeiro paint E antes dos useEffects dos componentes
  // filhos. Isso evita o bug onde o MetricChart (uPlot) era criado num momento
  // em que a class dark ainda não estava no DOM — o canvas saía vazio mesmo
  // com palette correto porque o browser ainda estava em colorscheme=light.
  useLayoutEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, m);
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(resolved === "dark" ? "light" : "dark");
  }, [resolved, setMode]);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
